import { zodResolver } from "@hookform/resolvers/zod";
import { KeyRound } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useErrorDialog } from "@/shared/components/ErrorDialog";
import { Button } from "@/shared/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import { useChangePassword } from "../api/settings.mutations";
import {
  type ChangePasswordInput,
  type ChangePasswordValues,
  changePasswordSchema,
} from "../settings.schemas";

export function AccountSettings() {
  const form = useForm<ChangePasswordInput, unknown, ChangePasswordValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });
  const changePassword = useChangePassword();
  const { showApiError } = useErrorDialog();
  const [done, setDone] = useState(false);

  const watched = form.watch();
  const allFilled =
    watched.currentPassword.length > 0 &&
    watched.newPassword.length > 0 &&
    watched.confirmPassword.length > 0;

  const onSubmit = (values: ChangePasswordValues) => {
    setDone(false);
    changePassword.mutate(
      {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      },
      {
        onSuccess: () => {
          form.reset();
          setDone(true);
        },
        onError: (error) =>
          showApiError(error, { title: "Kunde inte byta lösenord" }),
      },
    );
  };

  return (
    <div className="max-w-md space-y-6">
      <h2 className="text-lg font-semibold">Byt lösenord</h2>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="currentPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nuvarande lösenord</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    autoComplete="current-password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="newPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nytt lösenord</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    autoComplete="new-password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Bekräfta nytt lösenord</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    autoComplete="new-password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {done && (
            <p className="text-sm text-primary">Lösenordet har ändrats.</p>
          )}
          <Button
            type="submit"
            disabled={!allFilled}
            loading={changePassword.isPending}
          >
            <KeyRound />
            Byt lösenord
          </Button>
        </form>
      </Form>
    </div>
  );
}
